import { Link, router } from "@inertiajs/react"
import { useState } from "react"
import { ArrowRight, CheckCircle2, Loader2, RotateCcw, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

// Ascend slice 2 — a member takes a [[Quiz]] inside the lesson page. The
// server grades; this component only collects answers and shows the stored
// result. With `revealAnswers` off (a Final), results carry no answer key or
// rationale — only right or wrong — so there is nothing to show beyond that.

function initialAnswers(questions) {
  const answers = {}
  questions.forEach((q) => {
    answers[q.id] = q.questionType === "free_text" ? q.responseText || "" : []
  })
  return answers
}

function isAnswered(question, value) {
  return question.questionType === "free_text" ? value.trim().length > 0 : value.length > 0
}

function ChoiceList({ question, selected, onChange, result, disabled }) {
  const multi = question.questionType === "multi_select"
  const correctKeys = result?.correctKeys

  return (
    <div className={cn("grid gap-2", question.questionType === "true_false" ? "grid-cols-2" : "grid-cols-1")}>
      {question.choices.map((choice) => {
        const isSelected = selected.includes(choice.key)
        const isRight = correctKeys?.includes(choice.key)
        const isWrongPick = result && isSelected && correctKeys && !isRight
        return (
          <label
            key={choice.key}
            className={cn(
              "flex items-center gap-2 rounded-md border px-3 py-2 text-sm min-h-[44px] transition-colors",
              !disabled && "cursor-pointer",
              !result && isSelected && "border-kf-red bg-kf-red/5",
              !result && !isSelected && "border-zinc-200 hover:border-zinc-400",
              result && isRight && "border-green-500 bg-green-50 text-green-900",
              isWrongPick && "border-red-400 bg-red-50 text-red-900",
              result && !isRight && !isWrongPick && isSelected && "border-zinc-400 bg-zinc-50",
              result && !isRight && !isWrongPick && !isSelected && "border-zinc-200 text-zinc-500"
            )}
          >
            <input
              type={multi ? "checkbox" : "radio"}
              name={`question-${question.id}`}
              className="accent-kf-red h-4 w-4 shrink-0"
              checked={isSelected}
              disabled={disabled}
              onChange={() => {
                if (multi) {
                  onChange(isSelected ? selected.filter((k) => k !== choice.key) : [...selected, choice.key])
                } else {
                  onChange([choice.key])
                }
              }}
            />
            {choice.text}
          </label>
        )
      })}
    </div>
  )
}

function ResultBanner({ quiz, attempt }) {
  const passed = attempt.passed
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold",
        passed ? "bg-green-100 text-green-800" : "bg-red-50 text-red-800"
      )}
    >
      {passed ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
      {passed ? "Passed" : "Not a pass yet"} · {attempt.scorePercent}%
      <span className="font-normal opacity-75">({quiz.passPercent}% to pass · attempt {attempt.attemptNumber})</span>
    </div>
  )
}

function attemptsText(quiz) {
  if (quiz.attemptsLeft == null) return "Unlimited attempts"
  return `${quiz.attemptsLeft} of ${quiz.maxAttempts} attempt${quiz.maxAttempts === 1 ? "" : "s"} left`
}

export function QuizSection({ questions, quiz, courseId, lessonId, trackId = null, nextStep = null }) {
  const last = quiz.lastAttempt
  const [retaking, setRetaking] = useState(false)
  const [answers, setAnswers] = useState(() => initialAnswers(questions))
  const [submitting, setSubmitting] = useState(false)

  const showingResult = !!last && !retaking
  const resultsById = Object.fromEntries((showingResult ? last.results : []).map((r) => [r.questionId, r]))
  const allAnswered = questions.every((q) => isAnswered(q, answers[q.id]))

  const setAnswer = (id, value) => setAnswers((prev) => ({ ...prev, [id]: value }))

  const submit = () => {
    setSubmitting(true)
    router.post(
      `/app/member/courses/${courseId}/lessons/${lessonId}/quiz_attempts${trackId ? `?track=${trackId}` : ""}`,
      { answers },
      {
        // No onSuccess reset: a new attempt changes the parent's `key` and
        // remounts this component. A refused submit also redirects, and must
        // leave the member's answers on screen.
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
      }
    )
  }

  const retake = () => {
    setAnswers(initialAnswers(questions))
    setRetaking(true)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-zinc-900">Quiz</h2>
        <span className="text-xs text-zinc-500">
          {quiz.passPercent}% to pass · {attemptsText(quiz)}
        </span>
      </div>

      {showingResult && <ResultBanner quiz={quiz} attempt={last} />}

      <div className="space-y-4">
        {questions.map((question, index) => {
          const result = resultsById[question.id]
          const freeText = question.questionType === "free_text"
          return (
            <fieldset
              key={question.id}
              className={cn(
                "rounded-lg border bg-white p-4 space-y-3",
                result?.correct === true && "border-green-300",
                result?.correct === false && "border-red-300",
                !result && "border-zinc-200"
              )}
            >
              <legend className="sr-only">Question {index + 1}</legend>
              <p className="text-sm font-medium text-zinc-900">
                <span className="font-mono text-xs text-kf-red mr-2">Q{index + 1}.</span>
                {question.text}
              </p>

              {freeText ? (
                <Textarea
                  value={answers[question.id]}
                  onChange={(e) => setAnswer(question.id, e.target.value)}
                  placeholder="Write your answer..."
                  className="min-h-24"
                  disabled={showingResult}
                />
              ) : (
                <ChoiceList
                  question={question}
                  // With answers hidden (a Final), the result carries no picks.
                  selected={showingResult ? result?.selectedKeys || [] : answers[question.id]}
                  onChange={(value) => setAnswer(question.id, value)}
                  result={result}
                  disabled={showingResult}
                />
              )}

              {result && (
                <p className={cn("text-xs", result.correct ? "text-green-700" : "text-red-700")}>
                  {result.correct ? "Correct." : "Not correct."} {result.rationale}
                </p>
              )}
            </fieldset>
          )
        })}
      </div>

      <p className="text-xs text-zinc-500">Your coach can read your written answers.</p>

      <div className="flex flex-wrap items-center gap-3">
        {!showingResult && (
          <Button
            onClick={submit}
            disabled={submitting || !allAnswered || !quiz.canAttempt}
            variant="gradient"
            className="w-full sm:w-auto min-h-[44px]"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit quiz"
            )}
          </Button>
        )}
        {!showingResult && !allAnswered && (
          <span className="text-xs text-zinc-500">Answer every question to submit.</span>
        )}
        {/* In the track player, a pass leads straight on. */}
        {showingResult && last.passed && nextStep && !nextStep.locked && (
          <Button asChild variant="gradient" className="w-full sm:w-auto min-h-[44px]">
            <Link href={nextStep.href}>
              Next step: {nextStep.title}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        )}
        {showingResult && !last.passed && quiz.canAttempt && (
          <Button variant="outline" onClick={retake} className="min-h-[44px]">
            <RotateCcw className="h-4 w-4" />
            Retake quiz
          </Button>
        )}
        {showingResult && !last.passed && !quiz.canAttempt && (
          <span className="text-sm text-zinc-600">
            No attempts left. Ask an admin to reset your attempts.
          </span>
        )}
      </div>
    </div>
  )
}
